import React from 'react';
import { Box, Chip, Fade, Typography } from '@mui/material';
import { CheckCircle, Search } from '@mui/icons-material';

interface ContentMatchBadgeProps {
  contentTitle: string;
  matchedBy: 'explicit' | 'keywords' | 'default';
  matchedKeywords?: string[];
  show?: boolean;
}

/**
 * Visual feedback component that shows how content was matched
 * Appears briefly when content changes to help users understand the system
 */
const ContentMatchBadge: React.FC<ContentMatchBadgeProps> = ({
  contentTitle,
  matchedBy,
  matchedKeywords = [],
  show = true,
}) => {
  if (!show) return null;

  const getMessage = () => {
    switch (matchedBy) {
      case 'keywords':
        return matchedKeywords.length > 0
          ? `Matched keywords: ${matchedKeywords.slice(0, 3).join(', ')}`
          : 'Matched by keywords';
      case 'explicit':
        return 'Selected from menu';
      case 'default':
        return 'Default view';
      default:
        return '';
    }
  };

  const getIcon = () => {
    switch (matchedBy) {
      case 'keywords':
        return <Search fontSize="small" />;
      case 'explicit':
        return <CheckCircle fontSize="small" />;
      default:
        return null;
    }
  };

  return (
    <Fade in={show} timeout={500}>
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        <Chip
          icon={getIcon()}
          label={contentTitle}
          color={matchedBy === 'keywords' ? 'primary' : 'default'}
          sx={{
            fontFamily: 'var(--font-body)',
            fontWeight: 600,
            fontSize: '13px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            backgroundColor: matchedBy === 'keywords' ? 'var(--primary-blue)' : '#fff',
            color: matchedBy === 'keywords' ? '#fff' : 'var(--dark-gray)',
          }}
        />
        
        {matchedBy === 'keywords' && matchedKeywords.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              color: 'var(--dark-gray)',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              padding: '4px 8px',
              borderRadius: '4px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            }}
          >
            {getMessage()}
          </Typography>
        )}
      </Box>
    </Fade>
  );
};

export default ContentMatchBadge;

